import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import React from 'react'
import styled, { css } from 'styled-components'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  // These define the aspect ratio for the breakpoint given
  mobileAspectRatio?: string;
  tabletAspectRatio?: string;
  desktopAspectRatio?: string;
  largeDesktopAspectRatio?: string;
  mobileWidth?: string;
  tabletWidth?: string;
  desktopWidth?: string;
  largeDesktopWidth?: string;
  // Fills remaining height as well, treating the ratio as a 'minimum height' instead
  fillHeight?: boolean;
}

function getRatioAsPercentage(ratio: string) {
  // As aspect ratio can be formatted as a ratio e.g. "16:9",
  // or formatted as number meaning width/height, e.g. "1.77"
  if (ratio.includes(':')) {
    const [x, y] = ratio.split(':')
    return (+y / +x) * 100
  }
  else {
    return (1 / +ratio) * 100
  }
}

type RatioElementType = {
  $mobileAspectRatio?: string;
  $tabletAspectRatio?: string;
  $desktopAspectRatio?: string;
  $largeDesktopAspectRatio?: string;
  $mobileWidth?: string;
  $tabletWidth?: string;
  $desktopWidth?: string;
  $largeDesktopWidth?: string;
}

const xsRatioStyles = css<RatioElementType>`
  padding-bottom: ${({ $mobileAspectRatio: $xs }) => $xs && getRatioAsPercentage($xs)}%;
`

const smRatioStyles = css<RatioElementType>`
  ${mediaQueryUp.tablet} {
    padding-bottom: ${({ $tabletAspectRatio: $sm }) => $sm && getRatioAsPercentage($sm)}%;
    max-width: ${props => props.$tabletWidth};
  }
`

const mdRatioStyles = css<RatioElementType>`
  ${mediaQueryUp.desktop} {
    padding-bottom: ${({ $desktopAspectRatio: $md }) => $md && getRatioAsPercentage($md)}%;
    max-width: ${props => props.$desktopWidth};
  }
`

const lgRatioStyles = css<RatioElementType>`
  ${mediaQueryUp.largeDesktop} {
    padding-bottom: ${({ $largeDesktopAspectRatio: $lg }) => $lg && getRatioAsPercentage($lg)}%;
    max-width: ${props => props.$largeDesktopWidth};
  }
`

const xlRatioStyles = css<RatioElementType>`
  ${mediaQueryUp.largeDesktop} {
    padding-bottom: ${({ $largeDesktopAspectRatio: $xl }) => $xl && getRatioAsPercentage($xl)}%;
    max-width: ${props => props.$largeDesktopWidth};
  }
`

type SizerElementType = {
  $mobileWidth?: string;
  $tabletWidth?: string;
  $desktopWidth?: string;
  $largeDesktopWidth?: string;
}

const xsWidthStyles = css<SizerElementType>`
  max-width: ${props => props.$mobileWidth};
`

const smWidthStyles = css<SizerElementType>`
  ${mediaQueryUp.tablet} {
    max-width: ${props => props.$tabletWidth};
  }
`

const mdWidthStyles = css<SizerElementType>`
  ${mediaQueryUp.desktop} {
    max-width: ${props => props.$desktopWidth};
  }
`

const lgWidthStyles = css<SizerElementType>`
  ${mediaQueryUp.largeDesktop} {
    max-width: ${props => props.$largeDesktopWidth};
  }
`

const xlWidthStyles = css<SizerElementType>`
  ${mediaQueryUp.largeDesktop} {
    max-width: ${props => props.$largeDesktopWidth};
  }
`

const AspectRatioChildContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

/**
 * Normally against passing properties to a styled component
 * However this is the one exception where it's the best (nay, only?) solution
 * Could not work out a way to set the values required at different breakpoints
 */
const AspectRatioSizer = styled.div<SizerElementType>`
  width: 100%;

  &.fill-height {
    min-height: 100%;
  }

  ${props => !!props.$mobileWidth && xsWidthStyles}
  ${props => !!props.$tabletWidth && smWidthStyles}
  ${props => !!props.$desktopWidth && mdWidthStyles}
  ${props => !!props.$largeDesktopWidth && lgWidthStyles}
  ${props => !!props.$largeDesktopWidth && xlWidthStyles}
`

const AspectRatioElement = styled.div<RatioElementType>`
  position: relative;
  width: 100%;

  &.fill-height {
    min-height: 100%;
  }

  ${props => !!props.$mobileAspectRatio && xsRatioStyles}
  ${props => !!props.$tabletAspectRatio && smRatioStyles}
  ${props => !!props.$desktopAspectRatio && mdRatioStyles}
  ${props => !!props.$largeDesktopAspectRatio && lgRatioStyles}
  ${props => !!props.$largeDesktopAspectRatio && xlRatioStyles}
`

/**
 * This component reserves the space set out by the aspect ratios (and widths) given
 * This will often by used to give images space before they load in
 *
 * Please note it will default to full width if widths are not set
 */
function AspectRatio(props: Props) {
  const {
    children,
    mobileAspectRatio: xs,
    mobileWidth: xsWidth,
    tabletAspectRatio: sm,
    tabletWidth: smWidth,
    desktopAspectRatio: md,
    desktopWidth: mdWidth,
    largeDesktopAspectRatio: lg,
    largeDesktopWidth: lgWidth,
    className,
    fillHeight,
    ...rest
  } = props

  return <AspectRatioSizer
    {...rest}
    className={cn(className, { 'fill-height': fillHeight })}
    $mobileWidth={xsWidth}
    $tabletWidth={smWidth}
    $desktopWidth={mdWidth}
    $largeDesktopWidth={lgWidth}
  >
    <AspectRatioElement
      className={cn({ 'fill-height': fillHeight })}
      $mobileAspectRatio={xs}
      $tabletAspectRatio={sm}
      $desktopAspectRatio={md}
      $largeDesktopAspectRatio={lg}
      $mobileWidth={xsWidth}
      $tabletWidth={smWidth}
      $desktopWidth={mdWidth}
      $largeDesktopWidth={lgWidth}
    >
      <AspectRatioChildContainer>
        {children}
      </AspectRatioChildContainer>
    </AspectRatioElement>
  </AspectRatioSizer>
}

export default React.memo(AspectRatio)
